/**
 * Вкладка «Доставка» панели «Чатов»: доставка сделки к началу и к концу проката
 * (`GET /api/deals/{id}/delivery`), подсказки адреса, стоимость Яндекса и Dostavista
 * (`POST …/delivery/{start|end}/tariff` — только оценка) и сохранение
 * (`POST …/delivery/{start|end}` — пишет в боевой Bitrix и в договор CRM), заказ курьера
 * (`POST …/delivery/{start|end}/order` — тратит деньги компании, строка «Доставка» в договор).
 * Времена окна — местные салона. Спеки `docs/specs/2026-10-22-delivery-tab-design.md`,
 * `docs/specs/2026-10-22-delivery-order-design.md`.
 */
import { apiFetch, query } from "@/lib/api";
import type { ContractOutcome, DeliveryChange, DeliveryNow, DeliveryStatus, Direction, StatusKind } from "@/lib/delivery";

export type Geo = { lat: number; lon: number };
export type Salon = { name: string; short: string; address: string; lat: number | null; lon: number | null };
export type Parts = { street: string; apartment: string; entrance: string; floor: string; intercom: string };

export type DealDeliverySide = {
  day: string | null;
  status: string;
  status_label: string;
  status_kind: StatusKind;
  address: string;
  parts: Parts;
  window: { from: string | null; to: string | null } | null;
  /** Времена — местные салона, `ГГГГ-ММ-ДДTЧЧ:ММ`. */
  time: string | null;
  time_salon: string | null;
  courier: string;
  courier_phone: string;
  comment: string;
  geo: Geo | null;
  phone: string;
  note: string;
  /** Поля сделки как в Bitrix, времена — МСК: исходник формы «изменить». */
  bitrix: DeliveryNow;
};

export type DealDelivery = {
  deal_id: number;
  found: boolean;
  stage: string;
  tz: number;
  /** Номера контакта сделки (своя копия Bitrix) — первым подставляется в «Телефон для курьера». */
  phones: string[];
  salon: Salon;
  start: DealDeliverySide;
  end: DealDeliverySide;
  statuses: Record<Direction, DeliveryStatus[]>;
  changes: DeliveryChange[];
  can_edit: boolean;
  /** Заявки на курьера: по направлению — живая, иначе последняя. */
  orders: Orders;
  can_order: boolean;
};

export type OrderState = "new" | "search" | "found" | "pickup" | "delivered" | "cancelled" | "failed";
export type CourierOrder = {
  id: number;
  direction: Direction;
  provider: "yandex" | "dostavista";
  provider_label: string;
  external_id: string;
  variant: string;
  variant_label: string;
  price: number | null;
  client_price: number | null;
  status: string;
  state: OrderState;
  state_label: string;
  live: boolean;
  courier_name: string;
  car: string;
  courier_phone: string;
  courier: Geo | null;
  eta: string | null;
  tracking_url: string;
  contract_state: "" | "ok" | "off" | "no_contract" | "failed";
  contract_detail: string;
  stand: string;
  error: string;
  notified: Record<string, { at: string; to?: string[]; preview?: boolean; sent?: boolean }>;
  user_name: string;
  date: string | null;
};
export type Orders = {
  start: CourierOrder | null;
  end: CourierOrder | null;
  /** `PROKAT_DELIVERY_ORDER`: выключено — заявку не создаём. */
  enabled: boolean;
  /** Свой ключ Dostavista есть — её можно заказать. */
  dostavista: boolean;
  markup: number;
};

/** Что выбрали в расчёте: служба, вариант (`at` — Яндекс к началу окна) и цена службы. */
export type Pick = { provider: "yandex" | "dostavista"; variant: string; label: string; price: number };

export type Suggest = { title: string; subtitle: string; uri: string };

/** Форма вкладки: адрес — улица и дом, части — отдельно; `from`/`to` — часы окна (`from` −1 — начало не задано);
 * `salon_at` — «забрать из салона / вернуть в салон не позднее» по времени салона (пусто — не трогаем);
 * `comment` — общий комментарий курьеру на сделку. */
export type DeliveryForm = {
  salon_at: string;
  comment?: string | null;
  address: string;
  uri?: string | null;
  lat?: number | null;
  lon?: number | null;
  geo?: string | null;
  apartment: string;
  entrance: string;
  floor: string;
  intercom: string;
  day: string;
  from: number;
  to: number;
  phone: string;
  note: string;
};

export type Offer = {
  code: string;
  label: string;
  class: string;
  price: number;
  pickup_to: string | null;
  delivery_to: string | null;
};

export type Tariff = {
  point: (Geo & { geo: string }) | null;
  salon: Salon;
  /** `at_open` — с какого времени (местного) «к началу окна» можно заказать: Яндекс берёт отложенную заявку не дальше 7 ч. */
  yandex: { now: Offer[]; at: Offer[] | null; at_open?: string | null; error: string | null };
  dostavista: { fee?: number; total?: number; warnings?: string[]; stand?: string; skip?: string; error?: string };
};

export type DeliverySaved = {
  bitrix: "ok" | "same" | "pending";
  contract: ContractOutcome;
  changes: number;
  card: DealDelivery;
};

export const dealDeliveryKey = (deal: number) => ["deal-delivery", deal] as const;

export const loadDealDelivery = (deal: number) => apiFetch<DealDelivery>(`/api/deals/${deal}/delivery`);

export const suggestAddress = (deal: number, q: string) =>
  apiFetch<{ results: Suggest[]; off: boolean }>(`/api/deals/${deal}/delivery/suggest${query({ q })}`);

export const deliveryTariff = (deal: number, direction: Direction, body: DeliveryForm) =>
  apiFetch<Tariff>(`/api/deals/${deal}/delivery/${direction}/tariff`, { method: "POST", body: JSON.stringify(body) });

export const saveDealDelivery = (deal: number, direction: Direction, body: DeliveryForm) =>
  apiFetch<DeliverySaved>(`/api/deals/${deal}/delivery/${direction}`, { method: "POST", body: JSON.stringify(body) });

export type OrderResult = { order: CourierOrder; bitrix: string | null; bitrix_error?: string | null; card?: DealDelivery };

export const orderCourier = (
  deal: number,
  direction: Direction,
  body: DeliveryForm & { provider: string; variant: string; client_price: number; name: string },
) => apiFetch<OrderResult>(`/api/deals/${deal}/delivery/${direction}/order`, { method: "POST", body: JSON.stringify(body) });

export const cancelCourier = (deal: number, order: number, paid: boolean) =>
  apiFetch<{ order: CourierOrder; bitrix: string }>(`/api/deals/${deal}/delivery/order/${order}/cancel`, {
    method: "POST",
    body: JSON.stringify({ paid }),
  });

export const refreshCourier = (deal: number, order: number) =>
  apiFetch<{ order: CourierOrder }>(`/api/deals/${deal}/delivery/order/${order}/refresh`, { method: "POST" });

/** Цена клиенту: цена службы × наценку, вверх до 100 ₽ (как `order.цена_клиенту`). */
export const clientPrice = (price: number, markup = 2) => Math.ceil((price * markup) / 100) * 100;

/** Карта Яндекса виджетом (ключ не нужен): маршрут «откуда → куда» на машине, иначе — одна точка. */
export function mapUrl(from: Geo | null, to: Geo | null): string | null {
  if (from && to) return `https://yandex.ru/map-widget/v1/?rtext=${from.lat},${from.lon}~${to.lat},${to.lon}&rtt=auto`;
  const p = to ?? from;
  return p ? `https://yandex.ru/map-widget/v1/?pt=${p.lon},${p.lat},pm2rdm&z=15` : null;
}

/** Тот же маршрут на сайте Яндекс Карт — в новой вкладке, с полным масштабом. */
export function mapsUrl(from: Geo | null, to: Geo | null): string | null {
  if (from && to) return `https://yandex.ru/maps/?rtext=${from.lat},${from.lon}~${to.lat},${to.lon}&rtt=auto`;
  const p = to ?? from;
  return p ? `https://yandex.ru/maps/?pt=${p.lon},${p.lat}&z=15` : null;
}
